import React, { useState, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Size presets for the badge
const SIZE_PRESETS = {
  xs: { width: 20, height: 28 },
  sm: { width: 28, height: 40 },
  md: { width: 36, height: 52 },
  lg: { width: 48, height: 68 },
  xl: { width: 64, height: 90 }
};

export const RhinoOneBadge = ({ 
  size = 'md', 
  showTooltip = true,
  className = '',
  onClick = null
}) => {
  const [platformInfo, setPlatformInfo] = useState(null);
  const [badgeUrl, setBadgeUrl] = useState('/assets/branding/rhino-1-badge.png');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const fetchPlatformInfo = async () => {
      try {
        const response = await axios.get(`${API}/platform-info`);
        if (response.data) {
          setPlatformInfo(response.data);
          if (response.data.badge_url) {
            setBadgeUrl(response.data.badge_url);
          }
        }
      } catch (error) {
        // Use defaults if fetch fails
        console.log('Using default platform info');
      }
    };
    fetchPlatformInfo();
  }, []);

  const dimensions = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const version = platformInfo?.version || '1.0.0';
  const codename = platformInfo?.codename || 'RHINO-1';
  const buildDate = platformInfo?.build_date || new Date().toISOString().split('T')[0];

  const BadgeImage = (
    <div 
      className={`relative inline-flex items-center justify-center transition-all duration-200 ${className} ${onClick ? 'cursor-pointer' : ''}`}
      style={{ 
        width: dimensions.width, 
        height: dimensions.height,
        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <img 
        src={badgeUrl}
        alt="RHINO-1 Platform Badge"
        className="w-full h-full object-contain drop-shadow-sm"
        style={{
          filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
          transition: 'filter 0.2s ease'
        }}
        onError={(e) => {
          e.target.src = '/assets/branding/rhino-1-badge.png';
        }}
      />
    </div>
  );

  if (!showTooltip) {
    return BadgeImage;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {BadgeImage}
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="bg-slate-900 border border-blue-500/30 text-white px-3 py-2 shadow-lg"
        >
          <div className="text-center space-y-1">
            <div className="font-bold text-blue-400">{codename}</div>
            <div className="text-xs text-slate-300">Platform v{version}</div>
            <div className="text-xs text-slate-500">Build: {buildDate}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RhinoOneBadge;
